import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  SidenavbarComponent,
  ListItemComponent,
  LibTooltipDirective,
} from '@predigle/micro/component-ui';
import { ReactiveFormsModule } from '@angular/forms';
import { MapFilterComponent } from '../../components/map-filter/map-filter.component';
import { COMMUNITY_TABS, SubMenuService } from '../../../../services/submenu.service';
import { MapLayersComponent } from '../../components/map-layers/map-layers.component';
import { WaypointComponent } from '../../../create-waypoint/waypoint.component';
import { UserProfileComponent } from '../../components/user-profile/user-profile.component';
import { TourAnchorDirective } from '../../../../features/learning-center/engine/tour-anchor.directive';
import { VesselService } from '../../../../services/vessel.service';
import { ReplayService } from '../../../../services/replay.service';
import { UserRolesService } from '../../../../services/user-roles.service';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ComboService } from '../../components/combo/combo.service';
import { CHAT_UI_ENABLED } from '../../../../shared/feature-flags';

@Component({
  selector: 'app-side-navigation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SidenavbarComponent,
    ListItemComponent,
    LibTooltipDirective,
    MapFilterComponent,
    MapLayersComponent,
    WaypointComponent,
    UserProfileComponent,
    TourAnchorDirective,
  ],
  templateUrl: './side-navigation.component.html',
  styleUrl: './side-navigation.component.scss',
})
export class SideNavigationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tooltipRef') tooltipRef?: ElementRef<HTMLElement>;
  activeItem: string | null = null;
  hasCoords: boolean = false;
  /** Effective (rendered) state — collapses to the icon rail while a full-screen view is open. */
  isExpanded: boolean = true;
  /** The user's chevron preference, restored when returning to the map. */
  private userExpanded: boolean = true;
  /** True while any full-screen view / panel is open — hides the expand toggle. */
  anyOverlayOpen: boolean = false;
  /**
   * Label of the panel we have just requested but whose open-state has not
   * flipped to `true` yet (some entry points open asynchronously, e.g. Replay).
   * Without it the state-driven sync below would clear the highlight in the gap
   * between the click and the panel actually reporting itself open.
   */
  private pendingNavLabel: string | null = null;
  private overlaySub?: Subscription;
  private collapseSub?: Subscription;
  mapCenter: { lat: number; lng: number } | null = null;
  hoveredLabel: string | null = null;
  tooltipTop = 0;
  tooltipLeft = 0;

  isFiltersSubmenuOpen: boolean = true;
  activeFilterTab: 'filters' | 'styles' | null = null;
  hoveredFlyoutItem: any = null;
  flyoutTop = 0;
  flyoutLeft = 0;
  private flyoutCloseTimer: any = null;
  private filterSub?: Subscription;
  private filterTabSub?: Subscription;
  private profileTabSub?: Subscription;
  private lastOverlayStates?: boolean[];

  private readonly allFirstNavItems = [
    {
      label: 'Map Filters',
      icon: '/assets/home/shared/filter_logo.svg',
      isDisabed: false,
    },
    {
      label: 'Map Styles',
      icon: '/assets/home/shared/map_style_logo.svg',
      isDisabed: false,
    },
    {
      label: 'Layers',
      icon: '/assets/home/shared/layer_logo.svg',
      isDisabed: false,
    },
    {
      label: 'Waypoint',
      icon: '/assets/home/shared/wavepoint_logo.svg',
      isDisabed: false,
    },
    {
      label: 'Replay',
      icon: '/assets/home/shared/replay_logo.svg',
      isDisabed: false,
    },
  ];

  /** Replay is ambassador/admin only (Flutter `AdminGate.canAccessHomeTripReplay`); hidden until roles resolve. */
  firstNavItems = this.allFirstNavItems.filter((i) => i.label !== 'Replay');
  private rolesSub?: Subscription;

  // Messages is dropped from the rail while chat is hidden (CHAT_UI_ENABLED).
  downNavItems = [
    {
      label: 'Network',
      icon: '/assets/home/shared/network_logo.svg',
      isDisabed: false,
    },
    {
      label: 'Profile',
      icon: '/assets/home/shared/profile_logo.svg',
      isDisabed: false,
    },
    ...(CHAT_UI_ENABLED
      ? [
          {
            label: 'Messages',
            icon: '/assets/home/shared/messages_logo.svg',
            isDisabed: false,
          },
        ]
      : []),
    {
      label: 'Support',
      icon: '/assets/home/shared/customer-support.svg',
      isDisabed: false,
    },
    {
      label: 'Settings',
      icon: 'https://storybook.predigle.com/assets/settings_sidenav.svg',
      isDisabed: false,
    },
  ];

  constructor(
    public submenuService: SubMenuService,
    private vesselService: VesselService,
    private replayService: ReplayService,
    private routeService: Router,
    private renderer: Renderer2,
    private combo: ComboService,
    private userRoles: UserRolesService,
  ) { }
  ngOnInit() {
    this.loadPrimaryVesselCoords();
    this.rolesSub = this.userRoles.canAccessReplay$.subscribe((allowed) => {
      this.firstNavItems = this.allFirstNavItems.filter((i) => allowed || i.label !== 'Replay');
    });
    this.submenuService.mapCenter$.subscribe((center) => {
      this.mapCenter = center;
    });

    // Collapse to the icon rail whenever a full-screen view / panel is open OR
    // any on-map overlay is active (combo layers, or an applied ocean overlay).
    // While these are on, the icon-rail-only mode is *forced* — the expand
    // chevron is hidden (see `anyOverlayOpen` in the template) so the user
    // can't drag the panel open on top of the map they're looking at.
    this.overlaySub = combineLatest([
      this.submenuService.userProfileOpenState$,
      this.submenuService.messagesOpenState$,
      this.submenuService.filterOpenState$,
      this.submenuService.layerModalState$,
      this.submenuService.waypointModalState$,
      this.submenuService.watchPageModalState$,
      this.submenuService.replayMode$,
      this.combo.state$.pipe(map((s) => s.config.enabled)),
      this.submenuService.activeMapOverlays$.pipe(map((arr) => (arr?.length ?? 0) > 0)),
      // Last on purpose: a marker layer (buoys/tides/wrecks) lights the rail item but, as before,
      // does not force the sidebar collapsed the way a painted field does.
      this.submenuService.activeMapLayers$.pipe(map((arr) => (arr?.length ?? 0) > 0)),
    ]).subscribe((states) => {
      this.lastOverlayStates = states;
      this.anyOverlayOpen = states.slice(0, -1).some(Boolean);
      this.isExpanded = this.anyOverlayOpen ? false : this.userExpanded;
      this.syncActiveItem(states);
    });

    // Tab switches inside the open panel move the highlight between Network and Profile.
    this.profileTabSub = this.submenuService.activeProfileTab$.subscribe(() => {
      if (this.lastOverlayStates) this.syncActiveItem(this.lastOverlayStates);
    });

    // When an overlay is applied, collapse the sidebar to the icon rail so the
    // loader spinner and floating opacity widget aren't buried by the expanded
    // sidebar. Persist the preference so it stays collapsed after the overlay
    // panel closes.
    this.collapseSub = this.submenuService.collapseSidebar$.subscribe(() => {
      this.userExpanded = false;
      this.isExpanded = false;
    });

    this.filterSub = this.submenuService.filterOpenState$.subscribe((isOpen) => {
      if (!isOpen) {
        this.activeFilterTab = null;
      }
    });
    this.filterTabSub = this.submenuService.filterModalTab$.subscribe((tab) => {
      if (this.submenuService.currentFilterOpenState) {
        this.activeFilterTab = tab;
      }
    });
  }

  /**
   * Keep the rail highlight in sync with what is actually open.
   *
   * `activeItem` used to be write-only: set on click and cleared *only* by
   * clicking the brand logo. So any other way of leaving a panel — the panel's
   * own close button, opening a captain from a Watch post and backing out of
   * it, a route change — left the rail item lit while the user was looking at
   * the plain map (the "Profile stays highlighted on the map" report).
   *
   * The highlight now means "this panel/mode is open", derived from the same
   * states the sidebar already listens to, so it clears no matter how the panel
   * was dismissed. Profile / Settings / Support all ride the one profile panel,
   * so any of them stays lit while that panel is up.
   */
  private syncActiveItem(states: boolean[]): void {
    const [
      profileOpen,
      messagesOpen,
      filterOpen,
      layerOpen,
      waypointOpen,
      ,        // watch page — no rail item of its own
      replayMode,
      comboEnabled,
      overlaysApplied,
      pinLayerActive,
    ] = states;

    const openLabels = new Set<string>();
    if (filterOpen) {
      if (this.submenuService.currentFilterModalTab === 'styles') {
        openLabels.add('Map Styles');
      } else {
        openLabels.add('Map Filters');
        openLabels.add('Filters');
        openLabels.add('Filter');
      }
    }
    // The Layers item stays lit while any layer is painted on the map, not just while its picker
    // is open — the effect outlives the panel.
    if (layerOpen || comboEnabled || overlaysApplied || pinLayerActive) openLabels.add('Layers');
    if (waypointOpen) openLabels.add('Waypoint');
    if (replayMode) openLabels.add('Replay');
    if (messagesOpen) openLabels.add('Messages');
    if (profileOpen) {
      // One panel, two surfaces (Flutter ProfileSurface): Network is Watch/Captains/Fleets.
      if (COMMUNITY_TABS.includes(this.submenuService.currentProfileTab)) {
        openLabels.add('Network');
      } else {
        openLabels.add('Profile');
        openLabels.add('Settings');
        openLabels.add('Support');
      }
      // In-panel tab switches cross surfaces without a rail click; follow them.
      if (!this.activeItem || !openLabels.has(this.activeItem)) {
        this.activeItem = openLabels.has('Network') ? 'Network' : 'Profile';
      }
    }

    if (this.pendingNavLabel && openLabels.has(this.pendingNavLabel)) {
      this.pendingNavLabel = null;
    }
    if (
      this.activeItem &&
      this.activeItem !== this.pendingNavLabel &&
      !openLabels.has(this.activeItem)
    ) {
      this.activeItem = null;
    }
  }

  loadPrimaryVesselCoords() {
    this.vesselService.getVessels().subscribe((res: any) => {
      const vessels = Array.isArray(res) ? res : (res?.results || []);
      const primaryVessel = vessels.find((v: any) => v.is_primary === true);

      if (!primaryVessel) {
        this.hasCoords = false;
        return;
      }

      const vesselId = primaryVessel.id;

      this.vesselService.getLatestCoords(vesselId).subscribe((coords) => {
        if (coords && coords.lat && coords.lon) {
          this.hasCoords = true;
        } else {
          this.hasCoords = false;
        }
      });
    });
  }
  /** Maps a side-nav item to its Learning Center tour anchor id (or null). */
  anchorForItem(item: any): string | null {
    switch (item?.label) {
      case 'Map Filters':
      case 'Filters':
      case 'Filter':
        return 'home.filterButton';
      case 'Map Styles':
        return 'home.mapStylesButton';
      case 'Layers':
        return 'home.layersIcon';
      case 'Waypoint':
        return 'home.waypointButton';
      case 'Replay':
        return 'home.replayIcon';
      case 'Profile':
        return 'home.profileButton';
      case 'Messages':
        return 'home.chatIcon';
      default:
        return null;
    }
  }

  handleFilterSubmenuClick(tab: 'filters' | 'styles', event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.hideFlyout();
    this.activeItem = tab === 'styles' ? 'Map Styles' : 'Map Filters';
    this.pendingNavLabel = this.activeItem;
    this.activeFilterTab = tab;
    this.routeService.navigate(['/home']);
    this.submenuService.openFilterModal(tab);
  }

  handleNavClick(item: any) {
    if (item.label === 'Map Filters' || item.label === 'Filters' || item.label === 'Filter') {
      this.activeItem = 'Map Filters';
      this.pendingNavLabel = 'Map Filters';
      this.routeService.navigate(['/home']);
      this.submenuService.openFilterModal('filters');
      return;
    }

    if (item.label === 'Map Styles') {
      this.activeItem = 'Map Styles';
      this.pendingNavLabel = 'Map Styles';
      this.routeService.navigate(['/home']);
      this.submenuService.openFilterModal('styles');
      return;
    }

    this.activeItem = item.label;
    this.pendingNavLabel = item.label;
    if (item.label === 'Layers') {
      this.routeService.navigate(['/home']);
      this.submenuService.setLayerModalState(true);
    }
    if (item.label === 'Waypoint') {
      this.routeService.navigate(['/home']);
      this.submenuService.setWaypointModalState(true);
    }
    if (item.label === 'Replay') {
      this.routeService.navigate(['/home']);
      if (this.submenuService.isReplayActive) {
        // Toggling replay OFF — this click closes rather than opens, so drop the
        // highlight instead of waiting for a state that will never turn true.
        this.replayService.reset();
        this.submenuService.setReplayMode(false);
        this.activeItem = null;
        this.pendingNavLabel = null;
      } else {
        this.submenuService.requestOpenReplay();
      }
    }
    if (item.label === 'Network' || item.label === 'Profile') {
      // Land on the surface's first tab unless the panel is already on that surface.
      const onNetwork = COMMUNITY_TABS.includes(this.submenuService.currentProfileTab);
      if (item.label === 'Network' && !onNetwork) this.submenuService.navigateToProfileTab('watch');
      if (item.label === 'Profile' && onNetwork) this.submenuService.navigateToProfileTab('trips');
      this.submenuService.setFleetOverlay(false);
      this.submenuService.setDockmateAndFleetOverlay(false);
      this.submenuService.setUserProfileOpenState(true);
    }
    if (item.label === 'Messages') {
      this.routeService.navigate(['/chat']);
      this.submenuService.setMessagesOpenState(true);
    }
    if (item.label === 'Settings') {
      this.submenuService.navigateToProfileTab('settings');
      this.submenuService.setUserProfileOpenState(true);
    }
    if (item.label === 'Support') {
      this.submenuService.navigateToProfileTab('settings');
      this.submenuService.setUserProfileOpenState(true);
      setTimeout(() => this.submenuService.openSupportOverlay(), 300);
    }
  }

  goToMap() {
    this.activeItem = null;
    this.pendingNavLabel = null;
    this.routeService.navigate(['/home']);
    this.submenuService.setFilterOpenState(false);
    this.submenuService.setLayerModalState(false);
    this.submenuService.setWaypointModalState(false);
    this.submenuService.setUserProfileOpenState(false);
    this.submenuService.setMessagesOpenState(false);
    // The other-user profile and fleet-detail overlays are separate states from
    // the profile panel that hosts them, and they render `position: fixed; inset: 0`
    // with `pointer-events: all` at z-index 7600. Leaving them set meant clicking the
    // VectorMarine logo routed to /home underneath a full-screen overlay that still
    // swallowed every click — the "getting stuck when trying to navigate out of it"
    // report. This button is the app's escape hatch, so it must clear ALL of them.
    this.submenuService.setDockmateAndFleetOverlay(false);
    this.submenuService.setFleetOverlay(false);
  }

  toggleSidebar() {
    this.userExpanded = !this.userExpanded;
    this.isExpanded = this.userExpanded;
  }

  getSidebarStyle() {
    return {
      width: this.isExpanded ? '224px' : '80px',
      'background-color': 'var(--pi-bg-primary)',
      'border-right': '1px solid var(--pi-color-grey-300s)',
      'padding-top': '1rem',
    };
  }

  getCollapseBtnStyle() {
    return {
      top: '50%',
      left: this.isExpanded ? '201px' : '57px',
      transform: 'translateY(-50%)',
    };
  }

  ngAfterViewInit(): void {
    if (this.tooltipRef) {
      this.renderer.appendChild(document.body, this.tooltipRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.tooltipRef?.nativeElement?.remove();
    this.overlaySub?.unsubscribe();
    this.collapseSub?.unsubscribe();
    this.filterSub?.unsubscribe();
    this.filterTabSub?.unsubscribe();
    this.profileTabSub?.unsubscribe();
    this.rolesSub?.unsubscribe();
    clearTimeout(this.flyoutCloseTimer);
  }

  showTooltip(event: MouseEvent, label: string): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tooltipTop = rect.top + rect.height / 2;
    this.tooltipLeft = rect.right + 10;
    this.hoveredLabel = label;
  }

  hideTooltip(): void {
    this.hoveredLabel = null;
  }

  showFlyout(event: MouseEvent, item: any): void {
    if (this.isExpanded || !item.hasSubmenu) return;
    clearTimeout(this.flyoutCloseTimer);
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.flyoutTop = rect.top;
    this.flyoutLeft = rect.right + 8;
    this.hoveredFlyoutItem = item;
  }

  scheduleHideFlyout(): void {
    this.flyoutCloseTimer = setTimeout(() => {
      this.hoveredFlyoutItem = null;
    }, 250);
  }

  cancelHideFlyout(): void {
    clearTimeout(this.flyoutCloseTimer);
  }

  hideFlyout(): void {
    clearTimeout(this.flyoutCloseTimer);
    this.hoveredFlyoutItem = null;
  }
}
